import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import type { ScriptureRefData } from "./types";

const { fontFamily } = loadFont("normal", { weights: ["400", "600", "700"], subsets: ["latin"] });

const GOLD = "#D4A853";
const BG = "rgba(0,0,0,0.78)";
const BORDER_GOLD = "rgba(212,168,83,0.4)";

const ScrollIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 7h6M9 11h4" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// Gold shimmer sweep across the text after pill appears
export const ScriptureRefC: React.FC<{ data: ScriptureRefData; durationInFrames: number }> = ({ data, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeIn = interpolate(frame, [0, Math.round(0.3 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const slideX = interpolate(frame, [0, Math.round(0.3 * fps)], [34, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const fadeOut = interpolate(frame, [durationInFrames - Math.round(0.4 * fps), durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  // Shimmer: a bright gold stripe sweeps left→right once, starting at frame 6
  const SHIMMER_START = 6;
  const SHIMMER_DUR = Math.round(0.8 * fps);
  const shimmerX = interpolate(frame, [SHIMMER_START, SHIMMER_START + SHIMMER_DUR], [-60, 420], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
    easing: Easing.bezier(0.4, 0, 0.6, 1),
  });
  const shimmerOpacity = interpolate(frame,
    [SHIMMER_START, SHIMMER_START + 4, SHIMMER_START + SHIMMER_DUR - 4, SHIMMER_START + SHIMMER_DUR],
    [0, 1, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut, pointerEvents: "none" }}>
      <div style={{
        position: "absolute", top: 30, right: 30,
        transform: `translateX(${slideX}px)`,
        background: BG,
        border: `1px solid ${BORDER_GOLD}`,
        borderRadius: 28,
        padding: "8px 16px 8px 10px",
        backdropFilter: "blur(12px)",
        boxShadow: "0 3px 18px rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", gap: 8,
        maxWidth: 360,
        overflow: "hidden",
        position: "relative",
      }}>
        {/* shimmer stripe */}
        <div style={{
          position: "absolute",
          top: 0, bottom: 0,
          left: shimmerX,
          width: 60,
          background: "linear-gradient(to right, transparent, rgba(212,168,83,0.35), transparent)",
          opacity: shimmerOpacity,
          pointerEvents: "none",
          zIndex: 10,
        }} />
        <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}><ScrollIcon /></div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 700, fontFamily, lineHeight: 1 }}>
            {data.scripture}
          </div>
          {data.section && (
            <div style={{ color: "rgba(212,168,83,0.8)", fontSize: 10, fontWeight: 600, fontFamily, marginTop: 3 }}>
              {data.section}
            </div>
          )}
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
